import { useCallback, useState } from 'react';

import { TaskSubgroup } from '@this+that/models';
import log from 'loglevel';

import { useGetTaskSubgroups as useGetTaskSubgroupsService } from '../../services/taskSubgroups.service';
import { useInboxEntriesStore } from '../../store';
import { isAbortError } from '../../utils/isAbortError';
import { createRequestGuard } from '../requestGuard';

interface UseFetchTaskSubgroupsReturn {
  handleFetchTaskSubgroups: (taskGroupId: string) => Promise<TaskSubgroup[]>;
  fetchError: Error | null;
}

const taskSubgroupsRequestGuard = createRequestGuard(
  () => useInboxEntriesStore.getState().taskManager.activeTaskGroup.taskGroupId,
);

let taskSubgroupsController: AbortController | null = null;

const startTaskSubgroupsController = (): AbortController => {
  taskSubgroupsController?.abort();
  taskSubgroupsController = new AbortController();
  return taskSubgroupsController;
};

export function useFetchTaskSubgroups(): UseFetchTaskSubgroupsReturn {
  const [fetchError, setFetchError] = useState<Error | null>(null);

  const { getTaskSubgroups } = useGetTaskSubgroupsService();
  const { saveTaskSubgroups, setTaskSubgroupsLoading } = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup,
  );

  const handleFetchTaskSubgroups = useCallback(
    async (taskGroupId: string): Promise<TaskSubgroup[]> => {
      if (taskSubgroupsRequestGuard.isStaleRequest(taskGroupId)) {
        return [];
      }
      const requestId = taskSubgroupsRequestGuard.startRequest();
      const controller = startTaskSubgroupsController();
      setTaskSubgroupsLoading(true);
      setFetchError(null);
      try {
        const subgroups = await getTaskSubgroups({
          taskGroupId,
          signal: controller.signal,
        });
        if (
          taskSubgroupsRequestGuard.isStaleRequest(taskGroupId) ||
          taskSubgroupsRequestGuard.isSupersededRequest(requestId)
        ) {
          return subgroups;
        }
        saveTaskSubgroups(subgroups, taskGroupId);
        return subgroups;
      } catch (error) {
        if (isAbortError(error)) {
          return [];
        }
        const errorInstance =
          error instanceof Error
            ? error
            : new Error('Failed to fetch task subgroups');
        setFetchError(errorInstance);
        log.error('Error fetching task subgroups:', error);
        const alreadyFetchedForGroup =
          useInboxEntriesStore.getState().taskManager.activeTaskGroup
            .taskSubgroupsFetchedForTaskGroupId === taskGroupId;
        if (!alreadyFetchedForGroup) {
          saveTaskSubgroups([], taskGroupId);
        }
        return [];
      } finally {
        if (
          !taskSubgroupsRequestGuard.isStaleRequest(taskGroupId) &&
          !taskSubgroupsRequestGuard.isSupersededRequest(requestId)
        ) {
          setTaskSubgroupsLoading(false);
        }
      }
    },
    [getTaskSubgroups, saveTaskSubgroups, setTaskSubgroupsLoading],
  );

  return {
    handleFetchTaskSubgroups,
    fetchError,
  };
}
